import { socket_send } from './socket_commands';
import { postReq } from './http';
import { real_id } from 'src/helpers/interface_helpers';
import { tryApplyMockLightCommand } from 'src/helpers/dev/mock_light_commands';
import type { Device } from '../types/Device';

/**
 * Abstraction for making a cloud request for gen1 devices
 * @param device - Device object
 * @param set - What we are controlling
 * @param params - Request parameters
 * @param timeout - Timeout in milliseconds, optional; default is 6000
 */
export const gen1CloudReq = async (device: Device, set: string, params: {}, timeout: number = 6000) => {
	const mockResult = tryApplyMockLightCommand(device, set, params as Record<string, unknown>);
	if (mockResult) return mockResult;

	let res = undefined;
	const channel = device.category === 'emeter' ? 0 : device.channel;

	// Changing 'set' for emeters
	set = device.category === 'emeter' ? 'relay' : set;

	// WS request
	res = await gen1SocketReq(set, device.id, channel, params, timeout);

	// Fallback
	if (!res.isok) {
		res = await gen1HttpReq(
			set,
			real_id(device.id),
			channel,
			params,
			timeout,
			device.shared ? device.server : undefined,
		);
	}

	return res;
};

/**
 * Helper function to make a socket request
 */
const gen1SocketReq = async (
	set: string,
	deviceId: string,
	channel: number,
	params: {},
	timeout: number = 6000,
) => {
	let res = undefined;

	const socketParams = {
		set: set,
		id: channel,
		...params,
	};

	try {
		res = await socket_send(
			deviceId,
			socketParams,
			'Shelly:CommandRequest',
			undefined /* method */,
			undefined /* viaq */,
			timeout,
		);
	} catch (error) {
		console.log('socket_send error', error);

		if (error.error === 'Timeout') {
			res = error;
		}
	} finally {
		if (typeof res !== 'undefined') {
			// include isok property if missing
			if (!res?.hasOwnProperty('isok')) {
				res = {
					isok: !res.hasOwnProperty('error'),
					...res,
				};
			}
		} else {
			res = { isok: false, error: 'Unknown error' };
		}
	}

	return res;
};

/**
 * Helper function to make a HTTP request
 */
const gen1HttpReq = async (
	set: string,
	deviceId: string,
	channel: number,
	params: {},
	timeout: number = 6000,
	instance?: string,
) => {
	let res = undefined;

	const _params = {
		id: deviceId,
		channel: channel,
		...translateParamsToPhp(params),
	};
	const url = buildUrl(set);

	res = await postReq(url, _params, timeout, instance);

	return res;
};

/**
 * Helper function to build the URL for PHP HTTP API
 * @param set Method provided by the abstraction function
 * @returns Full url path
 */
const buildUrl = (set: string) => {
	let control = '';
	switch (set) {
		case 'relay':
			control = 'relay';
			break;

		case 'roller':
		case 'roller_to_pos':
			control = 'relay/roller';
			break;

		case 'light':
			control = 'light';
			break;

		case 'target_t':
		case 'valve':
			control = 'thermostat';
			break;

		default:
			control = 'relay';
			break;
	}

	let url = `/device/${control}/control`;
	// Change url for roller_to_pos command
	// It is control endpoint, but treated as settings request
	if (set === 'roller_to_pos') {
		url.replace('control', 'settings/topos');
	}

	return url;
};

/**
 * Helper function to translate parameters provided by the abstraction function to PHP HTTP API
 * @param params
 * @returns Translated params in format PHP API expects
 */
const translateParamsToPhp = (params: {}) => {
	const fastParams = {};
	Object.keys(params).forEach((key) => {
		switch (key) {
			case 'go':
				fastParams['direction'] = params[key];
				break;

			case 'value':
				fastParams['temp'] = params[key];
				break;

			default:
				fastParams[key] = params[key];
				break;
		}
	});

	return fastParams;
};
